/**
 * Alpine.js Text Editor Component
 * Provides inline editing functionality for text area fields
 */

import { editorBase } from './alpine-editor-base.js';
import { SM_BREAKPOINT, watchViewportBelow } from './breakpoints.js';
import { createTextCompositionTracker } from './text-composition-tracker.js';

/**
 * Text Editor Component Factory
 * Handles text area fields like description, hobbies, music, etc.
 */
export function textEditor() {
    return {
        // State
        isEditing: false,
        // Follows the viewport. A host that draws the form both inline and in a mobile
        // sheet shows the sheet only when this is true.
        inSheet: false,
        value: '',
        originalValue: '',
        isSaving: false,
        // The Symbol of the last successful save, sent with its saved event. A failed save keeps it.
        // An object token fails === through Alpine's reactive proxy, and a counter repeats across editors.
        saveToken: null,
        error: null,
        charCount: 0,
        compositionTracker: null,
        compositionSubmissionAttempt: null,
        // Declared so each editor owns them. Alpine's merged scope writes an undeclared key to the
        // outermost x-data scope. editorBase.initEditor() sets _timeoutIds.
        _stopSheetWatch: null,
        _charCountTimeout: null,
        _timeoutIds: null,

        // Config (set via x-init or Alpine data attributes)
        // maxLength / minLength come from data-max-length / data-min-length, which the
        // Twig macro renders from ProfilesText. The defaults here only apply to a host
        // element that omits the attributes.
        fieldName: '',
        maxLength: 5000,
        minLength: 0,
        endpoint: '',
        placeholder: '',
        supportsBBCode: true,
        minHeight: 100,
        
        // Computed properties
        get remainingChars() {
            return this.maxLength - this.charCount;
        },
        
        get hasChanges() {
            return this.value !== this.originalValue;
        },
        
        get displayValue() {
            if (!this.value) {
                return this.placeholder || 'Click to add content';
            }
            
            // Format with BBCode if supported
            if (this.supportsBBCode) {
                return editorBase.formatWithBBCode(this.value);
            }
            
            // Otherwise just convert newlines to breaks
            return this.value.replace(/\n/g, '<br>');
        },
        
        // Methods
        getTextarea() {
            if (window.innerWidth < SM_BREAKPOINT && this.$refs.textareaMobile) {
                return this.$refs.textareaMobile;
            }
            return this.$refs.textarea;
        },

        init() {
            // Initialize from element data attributes
            const element = this.$el;
            
            this.fieldName = element.dataset.field || this.fieldName;
            this.maxLength = parseInt(element.dataset.maxLength) || this.maxLength;
            this.minLength = parseInt(element.dataset.minLength) || this.minLength;
            this.placeholder = element.dataset.placeholder || this.placeholder;
            this.supportsBBCode = element.dataset.bbcode !== 'false';
            
            // Get endpoint based on field
            this.endpoint = element.dataset.endpoint || editorBase.getEndpointForField(this.fieldName);
            
            // Get initial value
            const contentEl = element.querySelector('.inline-edit-content');
            if (contentEl) {
                // Get raw value from data attribute - don't fall back to textContent for empty fields
                let rawValue = contentEl.dataset.rawValue || contentEl.dataset.value || '';
                
                // Decode HTML entities if the value came from a data attribute
                // (data attributes contain HTML-encoded values in the rendered HTML)
                if (contentEl.dataset.rawValue || contentEl.dataset.value) {
                    rawValue = editorBase.decodeHtml(rawValue);
                }
                
                this.originalValue = rawValue;
                this.value = this.originalValue;
            } else {
                // Try to get from store
                this.originalValue = this.$store.profile?.getFieldValue(this.fieldName) || '';
                this.value = this.originalValue;
            }
            
            this.charCount = this.value.length;

            if (this.fieldName === 'description') {
                this.compositionTracker = createTextCompositionTracker({
                    initialValue: this.value,
                    baseline: this.value ? 'preloaded_existing' : 'empty',
                });
            }
            
            // Initialize base utilities
            editorBase.initEditor(this);
            this._stopSheetWatch = watchViewportBelow(SM_BREAKPOINT, (below) => {
                this.inSheet = below;
            });
        },

        destroy() {
            this._stopSheetWatch?.();
        },
        
        // Scroll lock belongs to the ui.bottom_sheet_* host, which locks while its
        // alpine_show expression is true. Every close must call cancel() or save().
        startEdit() {
            this.isEditing = true;
            this.isSaving = false;
            this.error = null;

            // Register with store for tracking
            if (this.$store.profile) {
                this.$store.profile.startEditing(this.fieldName);
            }

            // Focus textarea after Alpine updates the DOM
            this.$nextTick(() => {
                window.LucideIcons?.initialize(this.$el);
                const textarea = this.getTextarea();
                if (textarea) {
                    this.compositionTracker?.attach(textarea);
                    textarea.focus();
                    // Set cursor at end
                    textarea.setSelectionRange(textarea.value.length, textarea.value.length);
                    // Auto-resize
                    this.autoResize();
                }
            });
        },
        
        async save() {
            // The textarea stays readonly and focusable during a save, so Ctrl+Enter reaches here.
            if (this.isSaving) {
                return;
            }

            // Validate against the limits this editor advertises, so the counter and
            // the rejection threshold can never be different numbers.
            //
            // A value can arrive longer than maxLength: the API ceiling is higher than
            // the editor limit, and other clients write these fields. maxlength caps
            // typing but never truncates a prefilled value. Hold such a value to its
            // own starting length so the owner can still correct or shorten it, and
            // only grows are refused. Without this, someone whose description came in
            // over the limit could not save any edit to it, including a deletion.
            const validation = editorBase.validateField(this.fieldName, this.value, {
                minLength: this.minLength,
                maxLength: Math.max(this.maxLength, this.originalValue.length),
            });
            if (!validation.valid) {
                this.error = validation.error;
                return;
            }
            
            // Don't save if no changes
            if (!this.hasChanges) {
                this.cancel();
                return;
            }
            
            this.isSaving = true;
            this.error = null;
            const token = Symbol('save');
            const editorValueAtSubmit = this.value;
            const submittedValue = this.fieldName === 'description' ? editorValueAtSubmit.trim() : editorValueAtSubmit;
            if (this.compositionTracker) {
                this.compositionSubmissionAttempt = this.compositionTracker.freezeAttempt(
                    submittedValue,
                    this.compositionSubmissionAttempt,
                );
            }
            const serializedComposition = this.compositionSubmissionAttempt?.serialized;
            const extraFields = typeof serializedComposition === 'string'
                ? {text_composition: serializedComposition}
                : {};
            
            try {
                // Save to server and wait for response
                await editorBase.saveField(this.endpoint, this.fieldName, submittedValue, extraFields);

                const valueAfterSave = this.value;
                this.originalValue = submittedValue;
                this.compositionTracker?.rebase(submittedValue, 'preloaded_existing');
                this.compositionSubmissionAttempt = null;
                
                // Update store
                if (this.$store.profile) {
                    this.$store.profile.updateField(this.fieldName, submittedValue);
                }
                this.saveToken = token;
                editorBase.emitSaved(this.fieldName, submittedValue, token);
                
                if (valueAfterSave === editorValueAtSubmit) {
                    this.value = submittedValue;
                    this.completeEdit();
                } else {
                    this.value = valueAfterSave;
                    this.compositionTracker?.recordValue(valueAfterSave, 'unknown');
                    this.charCount = valueAfterSave.length;
                }
                
            } catch (error) {
                // Show error and keep in edit mode
                this.error = error.message || 'Failed to save changes. Please try again.';
                console.error('Save error:', error);
            } finally {
                this.isSaving = false;
            }
        },
        
        cancel() {
            // Revert to original value
            this.value = this.originalValue;
            this.compositionTracker?.discard(
                this.originalValue,
                this.originalValue ? 'preloaded_existing' : 'empty',
            );
            this.compositionSubmissionAttempt = null;
            this.charCount = this.value.length;
            this.error = null;
            
            // Exit edit mode
            this.completeEdit();
        },
        
        completeEdit() {
            this.isEditing = false;

            // Unregister from store
            if (this.$store.profile) {
                this.$store.profile.stopEditing(this.fieldName);
            }
        },
        
        updateCharCount() {
            // A value back at the saved one leaves nothing to save, so a save error no longer applies.
            if (!this.hasChanges) {
                this.error = null;
            }

            // Debounce character count updates
            if (this._charCountTimeout) {
                clearTimeout(this._charCountTimeout);
            }
            
            this._charCountTimeout = setTimeout(() => {
                this.charCount = this.value.length;
                
                // Auto-resize if in edit mode
                if (this.isEditing) {
                    this.$nextTick(() => {
                        this.autoResize();
                    });
                }
            }, 100);
        },
        
        autoResize() {
            // Skip auto-resize on mobile — textarea scrolls internally in bottom sheet
            if (window.innerWidth < SM_BREAKPOINT) return;
            const textarea = this.getTextarea();
            if (textarea) {
                editorBase.autoResizeTextarea(textarea);
            }
        },
        
        handleKeydown(event) {
            // Save on Ctrl/Cmd + Enter
            if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
                event.preventDefault();
                this.save();
            }
            // Cancel on Escape. Escape does nothing during a save, so the editor keeps the value it sends.
            else if (event.key === 'Escape' && !this.isSaving) {
                event.preventDefault();
                this.cancel();
            }
        },
        
        // BBCode helper methods
        insertBBCode(tag) {
            const textarea = this.getTextarea();
            if (!textarea) return;
            
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const selectedText = this.value.substring(start, end);
            
            let replacement;
            if (tag === 'url') {
                const url = prompt('Enter URL:');
                if (url) {
                    replacement = selectedText ? 
                        `[url=${url}]${selectedText}[/url]` : 
                        `[url]${url}[/url]`;
                } else {
                    return;
                }
            } else {
                replacement = `[${tag}]${selectedText}[/${tag}]`;
            }
            
            // Insert the BBCode
            this.value = this.value.substring(0, start) + replacement + this.value.substring(end);
            this.compositionTracker?.recordValue(this.value, 'unknown');
            
            // Update char count
            this.updateCharCount();
            
            // Restore focus and selection
            this.$nextTick(() => {
                textarea.focus();
                const newCursorPos = start + replacement.length;
                textarea.setSelectionRange(newCursorPos, newCursorPos);
            });
        }
    };
}

// Register with Alpine if available
if (window.Alpine) {
    window.Alpine.data('textEditor', textEditor);
}
